import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { getPolicy, clearData } from '../redux/policyActions';
import { BackButton, Loader } from '../../../common/components';
import InternalContainer from '../../layout/containers/Internal';
import PolicyNotes from '../components/PolicyNotes/PolicyNotes';
import PolicyAudit from '../components/PolicyAudit/PolicyAudit';
import PolicyDetails from '../components/PolicyDetails/PolicyDetails';
import PolicyDocuments from '../components/PolicyDocuments/PolicyDocuments';
import PolicyEndorsements from '../components/PolicyEndorsements/PolicyEndorsements';
import PolicyEmails from '../components/PolicyEmails/PolicyEmails';
import PolicyAccountsList from '../components/PolicyAccounts/PolicyAccounts';
import InternalTabs from '../../layout/components/InternalTabs/InternalTabs';
import { setActiveTab } from '../../layout/redux/tabs';
import './styles/PolicyShow.scss';
import getIncludedResource from '../../../helpers/getIncludedResource';
import { isOwner } from '../../auth/redux/authActions';

const TAB_NAME = 'policyTabs';

const getTitles = (resource) => {
  if (resource.data.attributes.policy_number) {
    const version =
      resource.data.attributes.version > 1
        ? `(v${resource.data.attributes.version})`
        : '';
    return {
      title: `Policy number: ${resource.data.attributes.policy_number} ${version}`,
      type: 'Policy Details',
    };
  }

  if (resource.data.attributes.renewing_policy_number) {
    return {
      title: `Quote Reference: ${
        resource.data.attributes.quote_reference
      } - Renewing Policy:  ${resource.data.attributes.renewing_policy_number}`,
      type: 'Renewal Details',
    };
  }

  if (resource.data.attributes.mta_policy_number) {
    return {
      title: `Quote Reference: ${
        resource.data.attributes.quote_reference
      } - MTA on Policy:  ${resource.data.attributes.mta_policy_number}`,
      type: 'Mid Term Adjustment Details',
    };
  }

  return {
    title: `Quote Reference: ${resource.data.attributes.quote_reference}`,
    type: 'Quote Details',
  };
};

const isBrokerPolicy = (application) => {
  const broker = getIncludedResource(application.data, application.included, 'broker');
  return (
    broker && broker.data.attributes && broker.data.attributes.broker_type === 'broker'
  );
};

const PolicyShowContainer = ({
  location,
  params: { id, type },
  getPolicy,
  setActiveTab,
  clearData,
  policy,
  activeTab,
}) => {
  const activeTabFromLocationState = location?.state?.tab ?? 1;
  const isApplication = type === 'applications';

  useEffect(
    () => {
      if (activeTab === 1) {
        getPolicy(id, isApplication);
      }
    },
    [id, getPolicy, isApplication, activeTab],
  );

  useEffect(
    () => {
      setActiveTab(TAB_NAME, activeTabFromLocationState);
    },
    [activeTabFromLocationState, setActiveTab],
  );

  useEffect(
    () => {
      return () => {
        clearData();
      };
    },
    [clearData],
  );

  const resource = policy.resources[id];

  if (!resource) {
    return (
      <InternalContainer title={'Policy Details'}>
        <Loader />
      </InternalContainer>
    );
  }

  const titles = getTitles(resource);
  const reference = titles.title;
  const policyType = titles.type;
  const productName = resource.data.attributes.product_name;
  const isSmartLuggage = productName === 'Smart Luggage';

  const isPolicy = resource.data.attributes.policy_number;

  return (
    <InternalContainer title={policyType} buttons={<BackButton />}>
      <InternalTabs
        id={TAB_NAME}
        tabs={[
          {
            permissions: ['policy.view', 'policy.view_owner'],
            title: 'Details',
            content: (
              <PolicyDetails
                resource={resource}
                isBrokerPolicy={isOwner() && isBrokerPolicy(resource)}
                quotesHistory={policy?.quotesHistory?.data ?? []}
                reference={reference}
              />
            ),
          },
          {
            permissions: ['policy.view', 'policy.view_owner'],
            title: 'Documents',
            content: (
              <PolicyDocuments
                resource={resource}
                reference={reference}
                isGeneratingDocs={policy.isGeneratingDocs}
              />
            ),
          },
          {
            permissions: ['communication.view'],
            orgTypes: ['owner'],
            title: 'Emails',
            content: (
              <PolicyEmails
                policyId={resource.data.id}
                filterType={isPolicy ? 'policy' : 'policy_application'}
                reference={reference}
              />
            ),
          },
          {
            permissions: ['policy.mta'],
            title: 'Endorsements',
            content: resource.data.attributes.policy_number && !isSmartLuggage && (
              <PolicyEndorsements resource={resource} reference={reference} />
            ),
          },
          {
            permissions: ['policy.notes_view', 'policy.notes_create'],
            title: 'Notes',
            content: <PolicyNotes resource={resource} reference={reference} />,
          },
          {
            permissions: ['system.audit_logs_view'],
            title: 'Audit',
            content: <PolicyAudit resourceId={resource.data.id} reference={reference} />,
          },
          {
            permissions: ['account.view'],
            orgTypes: ['owner'],
            title: 'Accounts',
            content: isPolicy && (
              <PolicyAccountsList resource={resource} reference={reference} />
            ),
          },
        ]}
      />
    </InternalContainer>
  );
};

export default connect(
  (state) => ({
    activeTab: state.tabs?.activeTabs?.policyTabs,
    policy: state.policy,
  }),
  {
    getPolicy: getPolicy,
    setActiveTab: setActiveTab,
    clearData: clearData,
  },
)(PolicyShowContainer);
